import { Button } from 'components/atoms/Button';
import FileExtensionBadge from 'components/atoms/FileExtensionBadge.component';
import { Checkbox } from 'components/atoms/Form/Checkbox';
import NoResult from 'components/atoms/NoResult.component';
import type { onSort, sorter } from 'components/atoms/Table/table.types';
import { Tooltip } from 'components/atoms/Tooltip';
import UseEffect from 'components/atoms/UseEffect';
import TableBuilder from 'components/organisms/TableBuilder';
import type {
  TableCellLabelProps,
  TableCellRendererProps,
} from 'components/organisms/TableBuilder/TableBuilder.component';
import dayjs from 'dayjs';
import useGetRepositoryTags from 'hooks/DocumentsRepository/useGetRepositoryTags';
import useResetableState from 'hooks/useResetableState';
import useTableSelection from 'hooks/useTableSelection';
import useUIEffectsCache from 'hooks/useUIEffectsCache';
import { useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { DocumentItem, TagItemType } from 'types/repository.types';

interface DocumentsListProps {
  documents: DocumentItem[];
  sorter: sorter;
  onSort: onSort;
  selected?: string[];
  setSelected?: (ids: string[]) => void;
  highlight: string[];
  page: number;
  animated?: boolean;
  selectableRows?: boolean;
  onDelete?: (doc: DocumentItem) => void;
  onRowClick: (_id: string, item?: DocumentItem) => void;
  afterFlash?: (id: string) => void;
  flash?: string[];
}

const parseFileName = (
  filename: string,
  path: string,
): { name: string; ext: string } => {
  const lastFileNameDotIndex = filename.lastIndexOf('.');

  const lastFilePathDotIndex = path.lastIndexOf('.');

  const ext =
    lastFilePathDotIndex > 0 ? path.substring(lastFilePathDotIndex + 1) : '';

  return {
    name:
      lastFileNameDotIndex > 0
        ? filename.substring(0, lastFileNameDotIndex)
        : filename,
    ext,
  };
};

const SelectionRenderer: FC<TableCellRendererProps> = ({
  item,
  index,
  selected,
  setSelected,
  lastToggledIndex,
  setLastToggledIndex,
  selectableRowsIds,
}) => {
  const handleSelectOptions = useTableSelection();

  return (
    <Checkbox
      onClick={(event) => {
        const {
          currentTarget: { checked },
          shiftKey,
        } = event;

        event.stopPropagation();

        const newSelectedIds = handleSelectOptions({
          selectedIds: selected as string[],
          currentSelectIndex: index,
          lastToggledIndex,
          rowsIds: selectableRowsIds,
          checked,
          isPressingShift: shiftKey,
        });

        setSelected?.(newSelectedIds);

        setLastToggledIndex(index);
      }}
      checked={selected?.includes(item._id)}
      className="!text-primary"
    />
  );
};

const SelectAllRenderer: FC<TableCellLabelProps> = ({
  selected,
  setSelected,
  selectableRowsIds,
}) => {
  return (
    <Checkbox
      onClick={() => {
        setSelected?.(
          selected?.length === selectableRowsIds.length &&
            selectableRowsIds.length > 0
            ? []
            : selectableRowsIds,
        );
      }}
      checked={
        selected?.length === selectableRowsIds.length &&
        selectableRowsIds.length > 0
      }
      className="!text-primary"
    />
  );
};

const TagsRenderer: FC<TableCellRendererProps> = ({ item }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="w-full flex items-center justify-end flex-wrap gap-2">
      {!expanded && item?.tags?.length > 2 && (
        <Tooltip content={t('repository.documents_list.expand_tags_tooltip')}>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(true);
            }}
            asDefault
            className="py-1 px-3 rounded-full w-fit !text-primary !bg-primary-100"
          >
            <span className="font-semibold">+{item.tags.length - 2}</span>
          </Button>
        </Tooltip>
      )}

      {item?.tags
        ?.slice(0, expanded ? item?.tags?.length : 2)
        .map((tag: TagItemType) => (
          <div
            key={tag._id}
            style={{ backgroundColor: `${tag.color}17`, color: tag.color }}
            className="px-4 py-1.5 rounded-full w-fit"
          >
            <span className="font-semibold">{tag?.name}</span>
          </div>
        ))}
    </div>
  );
};

const DocumentsList: FC<DocumentsListProps> = ({
  documents,
  sorter,
  onSort,
  setSelected,
  selected = [],
  highlight,
  page,
  animated = true,
  selectableRows = true,
  onDelete,
  onRowClick,
  flash,
  afterFlash,
}) => {
  const { t } = useTranslation();

  const { effectsCache, setEffectsCache } = useUIEffectsCache();

  const animEffectCacheKey = `documents_repository_${page}`;

  const shouldStopPageAnimation = !animated || effectsCache[animEffectCacheKey];

  const [lastToggledIndex, setLastToggledIndex] = useResetableState({
    init: undefined,
    deps: [documents],
  });

  const selectableRowsIds = useMemo(
    () => documents.map((e: DocumentItem) => e._id),
    [documents],
  );

  const tagsQuery = useGetRepositoryTags();

  const columns = useMemo(
    () => [
      {
        key: 'selection',
        hidden: !selectableRows,
        Label: SelectAllRenderer,
        Renderer: SelectionRenderer,
      },
      {
        key: 'file.name',
        isSortable: true,
        Label: t('name'),
        thClassName: '!w-4/12',
        Renderer: (props: TableCellRendererProps) => {
          const { name } = parseFileName(
            props.item?.file?.name,
            props.item?.file?.path,
          );

          return (
            <div className="flex items-center">
              <p className="text-primary font-semibold">{name}</p>
            </div>
          );
        },
      },
      {
        key: 'file.type',
        isSortable: false,
        Label: t('type'),
        thClassName: '!w-1/12',
        Renderer: (props: TableCellRendererProps) => {
          const { ext } = parseFileName(
            props.item?.file?.name,
            props.item?.file?.path,
          );

          return <FileExtensionBadge extension={ext} />;
        },
      },
      {
        key: 'dateNumber',
        isSortable: true,
        Label: t('date'),
        thClassName: '!w-3/12',
        Renderer: (props: TableCellRendererProps) => (
          <div>
            {props.item?.dateNumber
              ? dayjs(String(props.item?.dateNumber), 'YYYYMMDD').format('ll')
              : null}
          </div>
        ),
      },
      {
        key: 'tags',
        contentClassName: '!text-right !justify-end',
        thClassName: '!w-4/12',
        isSortable: false,
        Label: t('documents_repository.filters.tags'),
        Renderer: TagsRenderer,
      },
    ],
    [selectableRows, t, tagsQuery.data],
  );

  if (!documents.length) return <NoResult wrapperClassName="!mt-20" />;

  return (
    <div className="w-full">
      <UseEffect
        effect={() => {
          const id = setTimeout(() => {
            if (animated) {
              setEffectsCache({ [animEffectCacheKey]: true });
            }
          }, 1250);

          return () => clearTimeout(id);
        }}
        deps={[shouldStopPageAnimation]}
        render={() => (
          <TableBuilder
            columns={columns}
            tableHeadClassName="bg-primary-50"
            sorter={sorter}
            onSort={onSort}
            data={documents}
            onRowClick={onRowClick}
            highlight={highlight}
            disableRowAnimation={shouldStopPageAnimation}
            setSelected={setSelected}
            selected={selected}
            lastToggledIndex={lastToggledIndex}
            setLastToggledIndex={setLastToggledIndex}
            selectableRowsIds={selectableRowsIds}
            flash={flash}
            afterFlash={afterFlash}
            color="primary"
          />
        )}
      />
    </div>
  );
};

export default DocumentsList;
